import React, { useMemo, useRef } from 'react'
import styled from 'styled-components'
import { useGetClassroomsQuery } from '@Features/LandingPage/LandingPageApi/LandingPageApi'
import { useGetEducationInfoQuery } from '@Features/Search/SearchApi/SearchApi'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ClassroomGridCard, Icon as AbwIcon } from '@Components/AbwaabPlus'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { SkeletonBox } from '@eduact/ed-components'

/**
 * Guest "الفصول المتاحة" — the design's browse cards in a swipeable carousel
 * (2 cards per mobile viewport, 4-up on desktop). Same /classroom/all data and
 * the same accessible-gated navigation the old card had.
 */
const GuestLandingClassrooms: React.FC = () => {
	const [t, i18n] = useTranslation('landingPage')
	const { t: tEdu } = useTranslation('educational_info')
	const { data, isLoading, isSuccess } = useGetClassroomsQuery()
	// The classroom payload carries years only; their parent education types come
	// from the /education/info tree (already fetched for the finder — cached).
	const { data: eduRes } = useGetEducationInfoQuery()
	const { isAuth } = useAppSelector(s => s.auth)
	const navigate = useNavigate()
	const railRef = useRef<HTMLDivElement>(null)
	const isRtl = i18n.dir() === 'rtl'

	// Page-scroll the rail; direction-aware so prev/next read correctly in RTL.
	const scrollRail = (dir: 'prev' | 'next') => {
		const el = railRef.current
		if (!el) return
		const amount = el.clientWidth * 0.8
		const sign = dir === 'prev' ? (isRtl ? 1 : -1) : isRtl ? -1 : 1
		el.scrollBy({ left: sign * amount, behavior: 'smooth' })
	}

	/** education-year id → parent education-type name */
	const yearTypeMap = useMemo(() => {
		const map = new Map<number, string>()
		for (const ty of eduRes?.data ?? []) {
			for (const y of ty.educationYears ?? []) map.set(y.id, ty.name)
		}
		return map
	}, [eduRes?.data])

	return (
		<Section>
			<HeadRow>
				<Title>{t('instructor.classrooms', { defaultValue: 'الفصول المتاحة' })}</Title>
				<HeadControls>
					<ArrowGroup>
						<RoundArrow type="button" aria-label="prev" onClick={() => scrollRail('prev')}>
							<span style={{ transform: isRtl ? 'scaleX(-1)' : 'none', display: 'flex' }}>
								<AbwIcon name="chevron-left" size={15} />
							</span>
						</RoundArrow>
						<RoundArrow type="button" aria-label="next" onClick={() => scrollRail('next')}>
							<span style={{ transform: isRtl ? 'none' : 'scaleX(-1)', display: 'flex' }}>
								<AbwIcon name="chevron-left" size={15} />
							</span>
						</RoundArrow>
					</ArrowGroup>
					<SeeAll type="button" onClick={() => navigate('/search?tab=classrooms')}>
						{t('courses.see_all', { defaultValue: 'عرض الكل' })}
						<Chevron>
							<AbwIcon name="chevron-left" size={14} />
						</Chevron>
					</SeeAll>
				</HeadControls>
			</HeadRow>
			<Rail ref={railRef}>
				{isLoading && Array.from({ length: 4 }).map((_, i) => <SkeletonBox key={i} isLoading width="100%" height="200px" />)}
				{isSuccess &&
					data &&
					data.data.map((classroom, index) => {
						const years = classroom.educationYears ?? []
						const yearLabels = years.map(y => tEdu(`academic_year.${y.name}`, { defaultValue: y.name }))
						const typeLabels = Array.from(new Set(years.map(y => yearTypeMap.get(y.id)).filter(Boolean))).map(name =>
							tEdu(`educational_type.${name}`, { defaultValue: name as string })
						)
						const teacher = `${classroom.instructor.user.first_name} ${classroom.instructor.user.last_name}`
						return (
							<ClassroomGridCard
								key={`${classroom.id}-${index}-guest-classrooms`}
								title={classroom.title}
								instructorName={teacher}
								typeLabels={typeLabels}
								yearLabels={yearLabels}
								sectionLabels={((classroom as any).educationSections ?? []).map((s: { name: string }) =>
									tEdu(`educational_field.${s.name}`, { defaultValue: s.name })
								)}
								mode={classroom.type === 'online' ? tEdu('online') : tEdu('center')}
								ctaLabel={tEdu('subscribe', { defaultValue: 'اشترك' })}
								photo={classroom.thumbnail}
								icon={classroom.category?.icon}
								enrolled={isAuth && !!classroom.enrolled && classroom.enrolled.length > 0}
								enrolledLabel={tEdu('enrolled', { defaultValue: 'مشترك' })}
								onClick={() => {
									if (!classroom.accessible) return
									navigate(`/classroom/${classroom.label}`)
								}}
							/>
						)
					})}
			</Rail>
		</Section>
	)
}

export default GuestLandingClassrooms

const Section = styled.section`
	padding: 12px 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`
const HeadRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`
const Title = styled.h2`
	margin: 0;
	font: var(--fw-bold) var(--fs-body-lg) var(--font-sans);
	color: var(--text-primary);
	@media (min-width: 1024px) {
		font-size: 22px;
	}
`
const HeadControls = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex: none;
`
/* Design's desktop rail controls: 28px round bordered prev/next buttons. */
const ArrowGroup = styled.div`
	display: none;
	@media (min-width: 1024px) {
		display: flex;
		gap: 4px;
	}
`
const RoundArrow = styled.button`
	width: 28px;
	height: 28px;
	border: 1px solid var(--border);
	border-radius: var(--r-full);
	background: var(--surface-card);
	color: var(--text-secondary);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: box-shadow var(--dur-fast) var(--ease);
	&:hover {
		box-shadow: var(--sh-hover);
	}
`
const SeeAll = styled.button`
	border: none;
	background: none;
	padding: 0;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 2px;
	font: var(--fw-bold) 13px var(--font-sans);
	font-family: var(--font-sans);
	color: var(--blue);
`
const Chevron = styled.span`
	display: flex;
	html:not([dir='rtl']) & {
		transform: scaleX(-1);
	}
`
/* 2 cards per mobile viewport (design grid columns), 4-up on desktop. */
const Rail = styled.div`
	display: flex;
	gap: 8px;
	overflow-x: auto;
	padding-bottom: 6px;
	scroll-snap-type: x proximity;
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
	> * {
		flex: 0 0 calc((100vw - 40px) / 2);
		max-width: 236px;
		scroll-snap-align: start;
	}
	/* Constant desktop card size — matches the 208px browse card everywhere. */
	@media (min-width: 1024px) {
		gap: 8px;
		> * {
			flex: 0 0 208px;
			max-width: none;
		}
	}
`
